import React, { useState, useEffect } from 'react';
import { NotesService, type Note } from '../services/notesService';

interface NotesProps {
  subjectId: string;
  subjectType: 'guide' | 'seeker';
  subjectName?: string;
}

const cardClasses =
  'rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-6 shadow-[inset_0_0_0_1px_rgba(44,39,49,0.05)]';

const primaryButtonClasses =
  'inline-flex items-center justify-center rounded-xl border border-[#2C2731] bg-[#2C2731] px-4 py-2 text-sm font-semibold text-[#FEF6D6] transition-colors hover:bg-[#3C3450] disabled:cursor-not-allowed disabled:opacity-50';

const secondaryButtonClasses =
  'inline-flex items-center justify-center rounded-xl border border-[#2C2731]/15 bg-[#F2EAC3] px-4 py-2 text-sm font-semibold text-[#2C2731] transition-colors hover:bg-[#F2E0A8]';

const textAreaClasses =
  'w-full rounded-xl border border-[#2C2731]/15 bg-[#FFFCEF] px-4 py-3 text-sm text-[#2C2731] placeholder:text-[#403851]/50 focus:outline-none focus:ring-2 focus:ring-[#2C2731]/40';

export const Notes: React.FC<NotesProps> = ({ subjectId, subjectType, subjectName }) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [newNote, setNewNote] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    loadNotes();
  }, [subjectId, subjectType]);

  const loadNotes = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const fetchedNotes = subjectType === 'guide' 
        ? await NotesService.getGuideNotes(subjectId)
        : await NotesService.getSeekerNotes(subjectId);
        
      setNotes(fetchedNotes);
    } catch (err) {
      console.error('Failed to load notes:', err);
      setError(err instanceof Error ? err.message : 'Failed to load notes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      setError('Please enter a note');
      return;
    }

    try {
      setIsAddingNote(true);
      setError('');

      const createdNote = subjectType === 'guide'
        ? await NotesService.createGuideNote(subjectId, newNote.trim())
        : await NotesService.createSeekerNote(subjectId, newNote.trim());

      // Refresh notes list
      await loadNotes();
      
      // Reset form
      setNewNote('');
      setShowAddForm(false);
    } catch (err) {
      console.error('Failed to add note:', err);
      setError(err instanceof Error ? err.message : 'Failed to add note');
    } finally {
      setIsAddingNote(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className={cardClasses}>
        <div className="animate-pulse space-y-4">
          <div className="h-4 w-1/4 rounded bg-[#F2E0A8]"></div>
          <div className="space-y-3">
            <div className="h-4 rounded bg-[#F2E0A8]"></div>
            <div className="h-4 w-3/4 rounded bg-[#F6E5B7]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${cardClasses} space-y-6`}>
      <div className="flex items-start justify-between gap-4 border-b border-[#2C2731]/10 pb-4">
        <h3 className="text-lg font-semibold text-[#2C2731]">
          Notes {subjectName && <span className="ph-mask">for {subjectName}</span>}
        </h3>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className={showAddForm ? secondaryButtonClasses : primaryButtonClasses}
        >
          {showAddForm ? 'Cancel' : 'Add Note'}
        </button>
      </div>

      {showAddForm && (
        <div className="rounded-2xl border border-[#2C2731]/10 bg-[#F2EAC3] p-4">
          <div className="space-y-3">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder={`Add a note about this ${subjectType}...`}
              className={textAreaClasses}
              rows={3}
            />

            {error && (
              <div className="rounded-xl border border-[#B24E4E]/40 bg-[#FCEDEA] px-3 py-2 text-sm text-[#B24E4E]">
                {error}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleAddNote}
                disabled={isAddingNote || !newNote.trim()}
                className={primaryButtonClasses}
              >
                {isAddingNote ? 'Adding...' : 'Add Note'}
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewNote('');
                  setError('');
                }}
                className={secondaryButtonClasses}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div>
        {notes.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-[#2C2731]/20 bg-[#FDF1C3] px-6 py-10 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[#2C2731]/10 bg-[#F2E0A8]">
              <svg className="h-6 w-6 text-[#403851]/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h4 className="text-base font-semibold text-[#2C2731]">No notes yet</h4>
              <p className="text-sm text-[#403851]">
                Add the first note about this {subjectType} to keep track of important information.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {notes.map((note) => (
              <div
                key={note.id}
                className="rounded-2xl border border-[#2C2731]/10 bg-[#FFFCEF] p-4 shadow-[inset_0_0_0_1px_rgba(44,39,49,0.02)]"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[#2C2731]/15 bg-[#F2E0A8] text-base font-semibold text-[#2C2731]">
                    {(note.leaver_name || note.leaver_email || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 space-y-2">
                    <div>
                      <p className="text-sm font-semibold text-[#2C2731] ph-mask">
                        {note.leaver_name || note.leaver_email || 'Unknown User'}
                      </p>
                      <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#403851]/80">
                        {note.leaver_user_type && (
                          <span className="capitalize">{note.leaver_user_type}</span>
                        )}{' '}
                        • {formatDate(note.created_at)}
                      </p>
                    </div>
                    <p className="text-sm text-[#2C2731] whitespace-pre-wrap ph-mask">{note.note_content}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
